import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Alert, Spinner } from "react-bootstrap";
import { 
  getAllTasks, 
  updateRecommendation, 
  updateRecommendationsByParentTaskId,
  getChildTasksByParentId 
} from "../services/APIClient";

function EditRecommendationModal({ show, onHide, recommendation, onSuccess, userName }) {
  const [formData, setFormData] = useState({
    taskId: "",
    name: "",
    url: "",
    description: "",
    countryid: "",
  });

  const [tasks, setTasks] = useState([]);
  const [childTasks, setChildTasks] = useState([]);
  const [selectedChildTaskId, setSelectedChildTaskId] = useState("");
  const [editScope, setEditScope] = useState("group"); // "group" or "individual"
  const [isLoadingTasks, setIsLoadingTasks] = useState(false);
  const [isLoadingChildTasks, setIsLoadingChildTasks] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});

  const isGroupedRecommendation = !!recommendation?.parentTaskId;

  useEffect(() => {
    if (show && recommendation) {
      setFormData({
        taskId: recommendation.taskId || "",
        name: recommendation.name || "",
        url: recommendation.url || "",
        description: recommendation.description || "",
        countryid: recommendation.countryid || "",
      });
      
      // Default to group scope for grouped recommendations
      setEditScope(isGroupedRecommendation ? "group" : "individual");
      setSelectedChildTaskId("");
      
      fetchTasks();
      
      // Fetch child tasks if this is a grouped recommendation
      if (isGroupedRecommendation && recommendation.parentTaskId) {
        fetchChildTasks(recommendation.parentTaskId);
      }
    }
  }, [show, recommendation]);

  const fetchTasks = async () => {
    setIsLoadingTasks(true);
    try {
      const response = await getAllTasks();
      const tasksData = Array.isArray(response) ? response : response.data || [];
      setTasks(tasksData);
    } catch (error) {
      console.error("Error fetching tasks:", error);
      setError("Failed to load tasks");
    } finally {
      setIsLoadingTasks(false);
    }
  };

  const fetchChildTasks = async (parentTaskId) => {
    setIsLoadingChildTasks(true);
    try {
      const children = await getChildTasksByParentId(parentTaskId);
      setChildTasks(children);
    } catch (error) {
      console.error("Error fetching child tasks:", error);
      setError("Failed to load child tasks");
    } finally {
      setIsLoadingChildTasks(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (validationErrors[name]) {
      setValidationErrors({ ...validationErrors, [name]: null });
    }
  };

  const handleTaskSelect = (e) => {
    const taskId = e.target.value;
    setFormData({ ...formData, taskId });
    
    if (validationErrors.taskId) {
      setValidationErrors({ ...validationErrors, taskId: null });
    }
  };

  const validate = () => {
    const errors = {};

    if (!isGroupedRecommendation && !formData.taskId) {
      errors.taskId = "Task is required";
    }

    if (editScope === "individual") {
      if (isGroupedRecommendation && !selectedChildTaskId) {
        errors.childTaskId = "Please select which task to ungroup";
      }
      if (!formData.name.trim()) {
        errors.name = "Name is required";
      }
    }

    if (!formData.url.trim()) {
      errors.url = "URL is required";
    } else if (!formData.url.startsWith("./docs/") && !formData.url.startsWith("http")) {
      errors.url = "URL must start with './docs/' or 'http'";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const payload = {
        url: formData.url,
        description: formData.description,
        countryid: formData.countryid,
        updated_by: userName,
      };

      if (isGroupedRecommendation && editScope === "group") {
        // Update all recommendations with this parentTaskId
        await updateRecommendationsByParentTaskId(recommendation.parentTaskId, payload);
      } else if (isGroupedRecommendation && editScope === "individual") {
        // Ungroup: convert to individual recommendation
        const selectedChild = childTasks.find(t => t.id === selectedChildTaskId);
        await updateRecommendation(recommendation.id, {
          parentTaskId: null, // Clear parent
          taskId: selectedChildTaskId, // Set specific task
          name: formData.name, // Use custom name
          ...payload
        });
      } else {
        // Update standalone recommendation
        await updateRecommendation(recommendation.id, {
          taskId: formData.taskId,
          name: formData.name,
          ...payload
        });
      }

      onSuccess();
    } catch (error) {
      console.error("Error updating recommendation:", error);
      setError(error.response?.data?.error?.message || "Failed to update recommendation");
    } finally {
      setIsSubmitting(false);
    }
  };

  const getParentTaskName = () => {
    return recommendation?.task?.name || recommendation?.name || 'this group';
  };

  if (!recommendation) {
    return null;
  }

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Edit Recommendation</Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {isGroupedRecommendation && (
            <Alert variant="info" className="mb-3">
              <div className="mb-2">
                <strong>Grouped Recommendation: "{getParentTaskName()}"</strong>
                <div className="small text-muted">
                  This recommendation applies to all child tasks in this group
                </div>
              </div>
              
              <Form.Check
                type="radio"
                id="scope-group"
                name="editScope"
                value="group"
                checked={editScope === "group"}
                onChange={(e) => setEditScope(e.target.value)}
                label={
                  <span>
                    <strong>Update entire group</strong>
                    <div className="small text-muted">Changes apply to all child tasks</div>
                  </span>
                }
                className="mb-2"
              />
              
              <Form.Check
                type="radio"
                id="scope-individual"
                name="editScope"
                value="individual"
                checked={editScope === "individual"}
                onChange={(e) => setEditScope(e.target.value)}
                label={
                  <span>
                    <strong>Ungroup and edit individually</strong>
                    <div className="small text-muted">
                      Convert to standalone recommendation for a specific task
                    </div>
                  </span>
                }
              />
            </Alert>
          )}

          {/* Show child task selector when ungrouping */}
          {isGroupedRecommendation && editScope === "individual" && (
            <Form.Group className="mb-3">
              <Form.Label>
                Select Task to Ungroup <span className="text-danger">*</span>
              </Form.Label>
              {isLoadingChildTasks ? (
                <div className="text-center py-3">
                  <Spinner animation="border" size="sm" />
                  <span className="ms-2">Loading child tasks...</span>
                </div>
              ) : (
                <Form.Select
                  value={selectedChildTaskId}
                  onChange={(e) => setSelectedChildTaskId(e.target.value)}
                  isInvalid={!!validationErrors.childTaskId}
                  required
                >
                  <option value="">Select which task to make standalone...</option>
                  {childTasks.map((task) => (
                    <option key={task.id} value={task.id}>
                      {task.name}
                    </option>
                  ))}
                </Form.Select>
              )}
              <Form.Control.Feedback type="invalid">
                {validationErrors.childTaskId}
              </Form.Control.Feedback>
              <Form.Text className="text-muted">
                This specific task will be ungrouped and given its own recommendation
              </Form.Text>
            </Form.Group>
          )}

          {/* Task field - locked for grouped, editable for standalone */}
          {!isGroupedRecommendation && (
            <Form.Group className="mb-3">
              <Form.Label>
                Task <span className="text-danger">*</span>
              </Form.Label>
              {isLoadingTasks ? (
                <div className="text-center py-3">
                  <Spinner animation="border" size="sm" />
                  <span className="ms-2">Loading tasks...</span>
                </div>
              ) : (
                <Form.Select
                  name="taskId"
                  value={formData.taskId}
                  onChange={handleTaskSelect}
                  isInvalid={!!validationErrors.taskId}
                  required
                >
                  <option value="">Select a task...</option>
                  {tasks.map((task) => (
                    <option key={task.id} value={task.id}>
                      {task.name}
                    </option>
                  ))}
                </Form.Select>
              )}
              <Form.Control.Feedback type="invalid">
                {validationErrors.taskId}
              </Form.Control.Feedback>
            </Form.Group>
          )}

          {/* Name field - only for individual recommendations */}
          {(!isGroupedRecommendation || editScope === "individual") && (
            <Form.Group className="mb-3">
              <Form.Label>
                Name <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="e.g., Rhyming, Letter Sound Knowledge"
                isInvalid={!!validationErrors.name}
                required
              />
              <Form.Control.Feedback type="invalid">
                {validationErrors.name}
              </Form.Control.Feedback>
              <Form.Text className="text-muted">
                The display name for this recommendation
              </Form.Text>
            </Form.Group>
          )}

          {/* Show parent name for group edits */}
          {isGroupedRecommendation && editScope === "group" && (
            <Form.Group className="mb-3">
              <Form.Label>Group Name</Form.Label>
              <Form.Control
                type="text"
                value={getParentTaskName()}
                disabled
                readOnly
              />
              <Form.Text className="text-muted">
                Parent task name is pre-configured and cannot be changed
              </Form.Text>
            </Form.Group>
          )}

          <Form.Group className="mb-3">
            <Form.Label>
              PDF URL <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              name="url"
              value={formData.url}
              onChange={handleChange}
              placeholder="./docs/Alpaca_Rhyming_Recommendations.pdf"
              isInvalid={!!validationErrors.url}
              required
            />
            <Form.Control.Feedback type="invalid">
              {validationErrors.url}
            </Form.Control.Feedback>
            <Form.Text className="text-muted">
              Path to the PDF file (e.g., ./docs/filename.pdf)
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Description (Optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Additional information about this recommendation..."
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Country ID (Optional)</Form.Label>
            <Form.Control
              type="text"
              name="countryid"
              value={formData.countryid}
              onChange={handleChange}
              placeholder="Leave blank for all countries"
            />
            <Form.Text className="text-muted">
              Only fill this if the recommendation is country-specific
            </Form.Text>
          </Form.Group>

          {editScope === "individual" && isGroupedRecommendation && selectedChildTaskId && (
            <Alert variant="warning" className="mb-3">
              <strong>Note:</strong> This will ungroup "{childTasks.find(t => t.id === selectedChildTaskId)?.name}" 
              from the parent group and create a standalone recommendation.
            </Alert>
          )}

          <div className="border-top pt-3 mt-3">
            <small className="text-muted">
              <strong>Last updated:</strong>{" "}
              {recommendation.updated_at 
                ? new Date(recommendation.updated_at).toLocaleString('en-IE', { 
                    day: '2-digit', 
                    month: '2-digit', 
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })
                : "N/A"}
              <br />
              <strong>Updated by:</strong> {recommendation.updated_by || "N/A"}
            </small>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                {editScope === "group" ? "Updating group..." : "Saving..."}
              </>
            ) : (
              <>
                {editScope === "group" && isGroupedRecommendation
                  ? "Update Group"
                  : "Save Changes"}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

export default EditRecommendationModal;